/**
 * @name getRandomNumber
 * @public
 * @description Generates a random integer between the minimum and maximum values (inclusive).
 * @param {number} [minimum=1] - The lower bound of the range
 * @param {number} [maximum=5] - The upper bound of the range
 * @returns {number} A random integer between minimum and maximum (inclusive)
 */
export function getRandomNumber(minimum = 1, maximum = 5) {
  const min = Math.ceil(minimum);
  const max = Math.floor(maximum);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * @name randomizeArray
 * @public
 * @description Shuffles the elements of an array into a random order (Fisher-Yates algorithm).
 * @param {Array} array - The array to randomize
 * @returns {Array} A new array with the same elements in random order
 */
export function randomizeArray(array) {
  const values = [...array];
  const results = [];
  while (values.length > 0) {
    const index = Math.floor(Math.random() * values.length);
    const value = values[index];
    values.splice(index, 1);
    results.push(value);
  }
  return results;
}

/**
 * @name getRandomFloat
 * @public
 * @description Generates a random floating point number between min and max.
 * @param {number} max - The upper bound of the range
 * @param {number} min - The lower bound of the range
 * @returns {number} A random float between min and max
 */
export const getRandomFloat = (max, min) => (Math.random() * (max - min) + min);

/**
 * @name getRandomDate
 * @public
 * @description Generates a random Date object between start and end dates.
 * @param {Date} [start=new Date(2000, 0, 1)] - The start date (default: January 1, 2000)
 * @param {Date} [end=new Date()] - The end date (default: current date)
 * @returns {Date} A random Date object between the start and end dates
 */
export const getRandomDate = (start = new Date(2000, 0, 1), end = new Date()) => new Date(
  start.getTime() + Math.random() * (end.getTime() - start.getTime()),
);

/**
 * @name generateRandomString
 * @public
 * @description Generates a random alphanumeric string with length between minLength and maxLength.
 * @param {number} minLength - The minimum length of the string
 * @param {number} maxLength - The maximum length of the string
 * @returns {string} A random string of alphanumeric characters
 */
export const generateRandomString = (minLength, maxLength) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
  let result = '';

  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};
